import React from "react";
import { useEffect, useState } from "react";

import moment from "moment";
import { Link } from "react-router-dom";

import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardMedia from "@material-ui/core/CardMedia";
import CardContent from "@material-ui/core/CardContent";
import Box from "@material-ui/core/Box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faThermometerHalf,
  faTint,
} from "@fortawesome/free-solid-svg-icons";

import {
  averageTemperature,
  averageHumidity,
  isTooHot,
  isTooCold,
  isTooDry,
  isTooWet,
} from "../utils/probeData";
import useTimeout from "hooks/useTimeout";
import addNotification from "react-push-notification";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: 280,
      [theme.breakpoints.down("sm")]: {
        minWidth: 300,
      },
      [theme.breakpoints.up("md")]: {
        minWidth: 300,
      },
      [theme.breakpoints.up("lg")]: {
        minWidth: 350,
      },
      [theme.breakpoints.up("xl")]: {
        minWidth: 400,
      },
    },
    media: {
      height: 0,
      paddingTop: "56.25%", // 16:9
    },
    cardContent: {
      display: "flex",
    },
    latestDataColumn: {
      flex: 1,
      justifyContent: "flex-start",
    },
    averageDataColumn: {
      flex: 1,
      justifyContent: "flex-end",
    },
    cardContentHeader: {
      marginBottom: 1,
    },
  })
);

interface props {
  probe: common.Probe;
}

export default function ProbeOverView(props) {
  const probe: common.Probe = props.probe;

  const tooHot = isTooHot(probe);
  const tooCold = isTooCold(probe);
  const tooDry = isTooDry(probe);
  const tooWet = isTooWet(probe);

  const classes = useStyles();
  const [lastUpdated, setLastUpdated] = useState<string>(
    moment.utc(probe.latestDateTime).fromNow()
  );

  useEffect(() => {
    const id = setInterval(() => {
      setLastUpdated(moment.utc(probe.latestDateTime).fromNow());
    }, 10000);
    return () => clearInterval(id);
  }, [[], probe.latestDateTime]);

  useEffect(() => {
    if (tooHot) {
      addNotification({
        title: `Too Hot`,
        subtitle: `${probe.name} is too hot`,
        message: `Maximum temperature is set at ${probe.maximumTemperature}, however the latest temperature reading is ${probe.latestTemperature}`,
        theme: "darkblue",
        native: true, // when using native, your OS will handle theming.
      });
    }

    if (tooCold) {
      addNotification({
        title: `Too Cold`,
        subtitle: `${probe.name} is too cold`,
        message: `Minimum temperature is set at ${probe.minimumTemperature}, however the latest temperature reading is ${probe.latestTemperature}`,
        theme: "darkblue",
        native: true, // when using native, your OS will handle theming.
      });
    }
  }, [probe?.latestTemperature]);

  const subHeader = (
    <span>
      <FontAwesomeIcon icon={faClock} /> {lastUpdated}
    </span>
  );

  return probe ? (
    <Link to={`probe/${probe.id}`} style={{ textDecoration: "none" }}>
      <Box>
        <Card className={classes.root}>
          <CardHeader title={probe.name} subheader={subHeader} />

          <CardMedia
            className={classes.media}
            image={`data:image/jpeg;base64,${probe.image}`}
            title={probe.name}
          />

          <CardContent className={classes.cardContent}>
            <div className={classes.latestDataColumn}>
              <h4 className={classes.cardContentHeader}>Latest</h4>
              <span style={{ paddingRight: 5 }}>
                <FontAwesomeIcon
                  title={tooHot ? "Too Hot" : tooCold ? "Too Cold" : ""}
                  color={tooHot ? "red" : tooCold ? "blue" : "black"}
                  icon={faThermometerHalf}
                />{" "}
                {probe.latestTemperature?.toFixed(2)}{" "}
              </span>

              {probe.hasHumidity && (
                <span>
                  <FontAwesomeIcon
                    title={tooDry ? "Too Dry" : tooWet ? "Too Wet" : ""}
                    color={tooDry ? "red" : tooWet ? "blue" : "black"}
                    icon={faTint}
                  />{" "}
                  {probe.latestHumidity}{" "}
                </span>
              )}
            </div>

            <div className={classes.averageDataColumn}>
              <h4 className={classes.cardContentHeader}>Daily Average</h4>
              <span style={{ paddingRight: 5 }}>
                <FontAwesomeIcon icon={faThermometerHalf} />{" "}
                {probe.averageDailyTemperature?.toFixed(2)}{" "}
              </span>
              {probe.hasHumidity && (
                <span>
                  <FontAwesomeIcon icon={faTint} /> {probe.averageDailyHumidity}{" "}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </Box>
    </Link>
  ) : (
    <div></div>
  );
}
